import { useMemo, useState } from 'react';

import { MediaType, SubjectType } from '@atom/types/media';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import FolderTree from './folderTree/FolderTree';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { ROOT_FOLDER_ID, ROOT_FOLDER_LABEL } from './constants';
import CreateMediaFolderModal from './CreateMediaFolderModal';
import EditMediaFolderModal from './EditMediaFolderModal';
import FilesTabContext from './FilesTabContext';
import FilesTabHeader from './FilesTabHeader';
import { buildBreadcrumbs, findFolderPath } from './filesTabUtilities';
import FolderBreadcrumbs from './FolderBreadcrumbs';
import MediaTable from './MediaTable';
import MediaTotalList from './MediaTotalList';
import MoveMediaFolderModal from './MoveMediaFolderModal';
import { ModalType } from './types';
import useFilesTabData from './useFilesTabData';

import classes from './filesTab.module.css';

type FilesTabProps = {
  subjectId: string;
  subjectType: SubjectType;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  onSelectedChange?: (selected: string[]) => void;
};

const FilesTab = ({
  subjectId,
  subjectType,
  canCreate,
  canUpdate,
  canDelete,
  onSelectedChange,
}: FilesTabProps) => {
  const [selectedFolderId, setSelectedFolderId] =
    useState<string>(ROOT_FOLDER_ID);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    new Set([ROOT_FOLDER_ID]),
  );
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalType>(ModalType.NONE);

  const {
    folderTree,
    folders,
    media,
    foldersLoading,
    mediaLoading,
    totals,
    createFolder,
    editFolder,
    deleteFolder,
    moveFolder,
    moveMedia,
    renameMedia,
    deleteMedia,
    bulkDeleteMedia,
    uploadAndRefetch,
    downloadSelected,
  } = useFilesTabData({
    subjectId,
    subjectType,
    folderId: selectedFolderId,
    mediaType,
    searchName: searchValue,
  });

  const folderPath = useMemo(
    () => findFolderPath(folderTree, selectedFolderId),
    [folderTree, selectedFolderId],
  );
  const selectedFolder = folderPath?.[folderPath.length - 1] ?? null;
  const selectedFolderDepth = folderPath ? folderPath.length - 1 : 0;
  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(folderTree, selectedFolderId),
    [folderTree, selectedFolderId],
  );
  const selectedFolderName = selectedFolder?.name ?? ROOT_FOLDER_LABEL;

  const emitSelection = (next: Set<string>) => {
    onSelectedChange?.([...next]);
  };

  const updateSelection = (next: Set<string>) => {
    setSelectedItems(next);
    emitSelection(next);
  };

  const toggleSelectItem = (id: string) => {
    updateSelection(toggleFromSet(selectedItems, id));
  };

  const clearSelection = () => {
    if (selectedItems.size > 0) {
      updateSelection(new Set());
    }
  };

  const selectFolder = (folderId: string) => {
    setSelectedFolderId(folderId);
    setMediaType(null);
    setSearchValue('');
    clearSelection();
  };

  const handleToggleExpand = (id: string) => {
    setExpandedIds(toggleFromSet(expandedIds, id));
  };

  const handleSelectMediaType = (type: MediaType) => {
    setMediaType(type);
    setSelectedFolderId('');
    setSearchValue('');
    clearSelection();
  };

  const handleCrumbClick = (id: string, index: number) => {
    if (index === breadcrumbs.length - 1) {
      return;
    }

    selectFolder(id === folderTree?.id ? ROOT_FOLDER_ID : id);
  };

  const handleDelete = async (folderId: string) => {
    await deleteFolder(folderId);

    if (folderId === selectedFolderId) {
      selectFolder(ROOT_FOLDER_ID);
    }
  };

  const handleMoveComplete = (movedFromId: string, destId: string) => {
    clearSelection();

    if (selectedFolderId === movedFromId) {
      selectFolder(destId);
    }
  };

  const handleSearchChange = (value: string) => {
    if (value && !mediaType && selectedFolderId !== ROOT_FOLDER_ID) {
      setSelectedFolderId(ROOT_FOLDER_ID);
      clearSelection();
    }

    setSearchValue(value);
  };

  const selectedTotalCount = mediaType ? totals[mediaType] : undefined;

  const contextValue = {
    folderTree,
    folders,
    media,
    foldersLoading,
    mediaLoading,
    uploadAndRefetch,
    downloadSelected,
    canCreate,
    canUpdate,
    canDelete,
    selectedFolderId,
    selectedFolder,
    selectedFolderDepth,
    selectedMediaType: mediaType,
    selectedItems,
    selectFolder,
    toggleSelectItem,
    clearSelection,
    onMoveComplete: handleMoveComplete,
    createFolder,
    editFolder,
    deleteFolder: handleDelete,
    moveFolder,
    moveMedia,
    renameMedia,
    deleteMedia,
  };

  return (
    <FilesTabContext.Provider value={contextValue}>
      <div className={classes.bodyContainer}>
        <div className={classes.leftBodyPaneFixed}>
          <div className={classes.leftPaneStack}>
            <div className={classes.foldersContainer}>
              <div className={classes.sectionHeader}>Folders</div>
              <FolderTree
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
              />
            </div>
            <div className={classes.fileCategoriesContainer}>
              <div className={classes.sectionHeader}>File Categories</div>
              <MediaTotalList
                selectedType={mediaType}
                totals={totals}
                selectMediaType={handleSelectMediaType}
              />
            </div>
          </div>
        </div>
        <div className={classes.rightBodyPaneFill}>
          <div className={classes.rightPaneHeader}>
            <FolderBreadcrumbs
              breadcrumbs={breadcrumbs}
              selectedType={mediaType}
              selectedCount={selectedTotalCount}
              onCrumbClick={handleCrumbClick}
            />
            <FilesTabHeader
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              openModal={setModal}
            />
          </div>
          <MediaTable />
        </div>

        <CreateMediaFolderModal
          open={modal === ModalType.CREATE_FOLDER}
          parentFolderId={selectedFolderId}
          onCreate={createFolder}
          closeModal={() => setModal(ModalType.NONE)}
        />
        {canUpdate && (
          <EditMediaFolderModal
            open={modal === ModalType.EDIT_FOLDER}
            folderId={selectedFolderId}
            initialName={selectedFolderName}
            onEdit={editFolder}
            closeModal={() => setModal(ModalType.NONE)}
          />
        )}
        {canDelete && (
          <ConfirmDeleteModal
            open={modal === ModalType.DELETE_FOLDER}
            title={`Delete ${selectedFolderName} folder`}
            onCancel={() => setModal(ModalType.NONE)}
            onConfirm={() => {
              handleDelete(selectedFolderId);
              setModal(ModalType.NONE);
            }}
            CancelButtonProps={{ 'data-cy': 'filesTabDeleteFolderCancel' }}
            ConfirmButtonProps={{ 'data-cy': 'filesTabDeleteFolderConfirm' }}
          >
            Are you sure you want to delete this folder and all the files within
            the folder?
          </ConfirmDeleteModal>
        )}
        {canDelete && (
          <ConfirmDeleteModal
            open={modal === ModalType.BULK_DELETE}
            title="Delete Files"
            onCancel={() => setModal(ModalType.NONE)}
            onConfirm={async () => {
              await bulkDeleteMedia([...selectedItems]);
              clearSelection();
              setModal(ModalType.NONE);
            }}
            CancelButtonProps={{ 'data-cy': 'filesTabBulkDeleteCancel' }}
            ConfirmButtonProps={{ 'data-cy': 'filesTabBulkDeleteConfirm' }}
          >
            Are you sure you want to delete these files?
          </ConfirmDeleteModal>
        )}
        <MoveMediaFolderModal
          open={modal === ModalType.MOVE_MEDIA}
          type="media"
          ids={[...selectedItems]}
          closeModal={() => setModal(ModalType.NONE)}
          disabled={[selectedFolderId]}
        />
      </div>
    </FilesTabContext.Provider>
  );
};

export default FilesTab;
